import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook for subscription plans management
 * Handles plans data, CRUD operations, and form state
 */
export const useSubscriptionPlans = (activeTab) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAssignPlan, setShowAssignPlan] = useState(false);
  const [selectedUserForPlan, setSelectedUserForPlan] = useState(null);
  const [planFormData, setPlanFormData] = useState({
    name: '',
    plan_type: 'monthly',
    credits_per_month: '',
    price: '',
    description: ''
  });
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Fetch subscription plans
  const fetchSubscriptionPlans = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/subscription-plans`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setSubscriptionPlans(response.data);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };
  
  // Create subscription plan
  const createSubscriptionPlan = async (planData) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/subscription-plans`,
        planData,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 201) {
        await fetchSubscriptionPlans();
        setShowAddPlan(false);
        setPlanFormData({
          name: '',
          plan_type: 'monthly',
          // Currency support (NEW)
          currency: 'INR',
          currency_symbol: '₹',
          // Credit allocation (ENHANCED)
          credits_per_month: '',
          credits_granted: '',
          price: '',
          description: '',
          // Visibility control (NEW)
          is_visible_to_users: true
        });
        return { success: true, message: 'Plan created successfully' };
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error creating plan' 
      };
    }
  };
  
  // Update subscription plan
  const updateSubscriptionPlan = async (planId, planData) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/subscription-plans/${planId}`,
        planData,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        await fetchSubscriptionPlans();
        setEditingPlan(null);
        setPlanFormData({
          name: '',
          plan_type: 'monthly',
          // Currency support (NEW)
          currency: 'INR',
          currency_symbol: '₹',
          // Credit allocation (ENHANCED)
          credits_per_month: '',
          credits_granted: '',
          price: '',
          description: '',
          // Visibility control (NEW)
          is_visible_to_users: true
        });
        return { success: true, message: 'Plan updated successfully' };
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error updating plan' 
      };
    }
  };
  
  // Delete subscription plan
  const deleteSubscriptionPlan = async (planId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/subscription-plans/${planId}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        await fetchSubscriptionPlans();
        return { success: true, message: 'Plan deleted successfully' };
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error deleting plan' 
      };
    }
  };
  
  /**
   * Assign a subscription plan to a user
   * 
   * FIXED: Payload structure issue (2024-07-29)
   * 
   * @param {Object} assignmentData - The assignment data object
   * @param {string} assignmentData.user_id - UUID of the user to assign plan to
   * @param {string} assignmentData.plan_id - UUID of the subscription plan to assign
   * @param {string} [assignmentData.assigned_by] - Optional: Username of admin assigning the plan
   * 
   * Backend API expects:
   * {
   *   "user_id": "2a42bbc8-681f-4dbe-a8f1-32f21b52ed24",
   *   "plan_id": "some-plan-uuid",
   *   "assigned_by": "superadmin"
   * }
   * 
   * Previous Issue: Component was passing (userObject, planId) as separate parameters
   * Fixed: Component now constructs proper object before calling this function
   */
  const assignPlanToUser = async (assignmentData) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/user-subscriptions`,
        assignmentData,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 201) {
        setShowAssignPlan(false);
        setSelectedUserForPlan(null);
        return { success: true, message: 'Plan assigned successfully' };
      }
    } catch (error) {
      console.error('Error assigning plan:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error assigning plan' 
      };
    }
  };
  
  // Effect to fetch plans when needed
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptionPlans();
    }
  }, [activeTab]);
  
  return {
    subscriptionPlans,
    showAddPlan,
    setShowAddPlan,
    editingPlan,
    setEditingPlan,
    showAssignPlan,
    setShowAssignPlan,
    selectedUserForPlan,
    setSelectedUserForPlan,
    planFormData,
    setPlanFormData,
    createSubscriptionPlan,
    updateSubscriptionPlan,
    deleteSubscriptionPlan,
    assignPlanToUser,
    fetchSubscriptionPlans
  };
};